import React, { useEffect, useState, useContext, useRef } from "react";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { isMobile } from "react-device-detect";
import { toast } from "react-toastify";

import useSettings from "../../../hooks/settings";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import { friendlyError } from "../../../middleware/common-functions";
import NavMob from "../../nav/headerMob";
import OrderPage from "./order";
import PayOut from "./payout/payout";
import SplitPayout from "./payout/splitPayout";
import { PrinterContext } from "../../../context/printer-context";
import {
  GETORDER,
  PAYORDER,
  PAY_SPLIT_ORDER,
  UPDATEORDER,
  UPDATEPRODUCT,
  UPDATETABLE,
  CREATE_PAYMENT_LINK,
} from "./queries";
import { GETCLIENTS, SAVEUSER } from "../pos/queries";
import { ModalTables, AddUserModal, AddUserDataModal } from "../../modals";
import { CancelOrderModal } from "../../modals/cancelOrder";

const Order = ({ history }) => {
  const timerRef = useRef(null);
  const tables = JSON.parse(localStorage.getItem("tables"));

  const [settings] = useSettings();
  const [modals, setModals] = useState({});
  const [loading, setLoading] = useState(true);
  const [shake, setShake] = useState(true);
  const [calculator, setCalculator] = useState({});
  const [status, setStatus] = useState(null);
  const [order, setOrder] = useState({});
  const [payOrderMutation] = useMutation(PAYORDER);
  const [updateOrderMutation] = useMutation(UPDATEORDER);
  const [updateTableMutation] = useMutation(UPDATETABLE);
  const [saveUserMutation] = useMutation(SAVEUSER);
  const [paySplitOrderMutation] = useMutation(PAY_SPLIT_ORDER);
  const [tip, setTip] = useState(0);
  const [tipInput, setTipInput] = useState("");
  const [tipFocused, setTipFocused] = useState(false);
  const handleTipChange = (value) => {
    setTipInput(value);
    const parsed = parseFloat(value);
    const tipValue = isNaN(parsed) ? 0 : parsed;
    setTip(tipValue);
    if (calculator.amount) {
      const set_calculator = { ...calculator };
      set_calculator.cash_diff = order.total + tipValue - parseFloat(calculator.amount);
      setCalculator({ ...set_calculator });
    }
  };
  const [splitMode, setSplitMode] = useState(false);
  const [splitPayments, setSplitPayments] = useState([]);
  const [splitPaying, setSplitPaying] = useState(false);
  const [buyer, setBuyer] = useState(null);
  const [updateProductMutation] = useMutation(UPDATEPRODUCT);
  const [createPaymentLinkMutation, { loading: creatingLink }] = useMutation(CREATE_PAYMENT_LINK);
  const [paymentLinkEmail, setPaymentLinkEmail] = useState("");
  const [paymentLinkName, setPaymentLinkName] = useState("");
  const printer = useContext(PrinterContext);
  const [getClients, { data: clients }] = useLazyQuery(GETCLIENTS, {
    fetchPolicy: "no-cache",
  });
  const { order_id } = useParams();
  // Split bill by products mode — restore from localStorage
  const splitBillStorageKey = `splitBill_${order_id}`;
  const savedSplitBill = (() => {
    try {
      return JSON.parse(localStorage.getItem(splitBillStorageKey) || "null");
    } catch { return null; }
  })();
  const [splitBillMode, setSplitBillMode] = useState(savedSplitBill ? true : false);
  const [selectedProducts, setSelectedProducts] = useState(savedSplitBill?.selectedProducts || {});
  const [billSplits, setBillSplits] = useState(savedSplitBill?.billSplits || []);
  const [billPaymentType, setBillPaymentType] = useState("cash");
  const [billCardType, setBillCardType] = useState(undefined);
  // Comanda selection mode
  const [comandaSelectMode, setComandaSelectMode] = useState(false);
  const [comandaSelectedProducts, setComandaSelectedProducts] = useState({});
  const order_id_int = parseInt(order_id);
  const { data: order_db, loading: order_loading } = useQuery(GETORDER, {
    variables: { order_id: order_id_int },
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    ///update localStorage orders
    const local_orders = JSON.parse(localStorage.getItem("all_orders") || "[]");
    const order_index = local_orders.findIndex((o) => o.id === order_id);
    local_orders[order_index] = { ...order };
    localStorage.setItem("all_orders", JSON.stringify(local_orders));
  }, [order, order_id]);

  useEffect(() => {
    if (order_db) {
      setOrder(order_db.getOrder);
      setLoading(false);
    }
  }, [order_db]);

  // Persist split bill state to localStorage
  useEffect(() => {
    if (splitBillMode) {
      localStorage.setItem(splitBillStorageKey, JSON.stringify({
        selectedProducts,
        billSplits,
      }));
    }
  }, [splitBillMode, selectedProducts, billSplits, splitBillStorageKey]);

  useEffect(() => {
    if (shake) {
      setTimeout(() => {
        setShake(false);
      }, 500);
    }
  }, [shake]);

  const payOut = () => {
    setStatus("payout");
  };

  const payOutBack = () => {
    setStatus(null);
  };

  const addUp = (add) => {
    if (tipFocused) {
      let tipAmount = tipInput || "";
      if (isNaN(add) && add !== ".") {
        tipAmount = tipAmount.slice(0, -1);
      } else {
        tipAmount = tipAmount + add;
      }
      handleTipChange(tipAmount);
      return;
    }
    const set_calculator = { ...calculator };
    let amount = set_calculator.amount || "";
    if (isNaN(add) && add !== ".") {
      amount = amount.slice(0, -1);
    } else {
      amount = amount + add;
    }
    set_calculator.cash_diff = order.total + tip - parseFloat(amount);
    set_calculator.amount = amount;
    setCalculator({ ...set_calculator });
  };

  const setPaymentType = async (ev) => {
    const set_order = { ...order };
    const type = ev.currentTarget.dataset.type;
    const card_type = ev.currentTarget.dataset.card;
    set_order.payment_type = type;
    set_order.card_type = card_type || undefined;
    setOrder({ ...set_order });
  };

  const payOrder = async (ev) => {
    const set_calculator = { ...calculator };
    const set_order = { ...order };
    if (set_calculator.cash_diff > 0 && set_order.payment_type === "cash") {
      setShake(true);
    }
    let total_income = set_calculator.amount || set_order.total;
    const tipValue = parseFloat(tip) || 0;
    const paymentInput = {
      order_id: parseInt(order_id),
      cash: total_income ? parseFloat(total_income) : set_order.total,
      type: set_order.payment_type,
      card_type: set_order.card_type,
      tip: tipValue,
    };
    try {
      const { data: updatedOrder } = await payOrderMutation({
        variables: { payment: paymentInput },
      });
      set_order.payed = updatedOrder.payOrder.payed;
      set_order.cash_income = updatedOrder.payOrder.cash_income;
      setOrder({ ...set_order });
      setStatus(null);
      ///print customer ticket
      if (set_order.payed) {
        if (settings.printer_autprint !== false) {
          printer.print(set_order, "client");
        }
        if (!settings.avoid_fulfilled) {
          updateOrder("fulfilled");
        }
        history.push("/");
      }
    } catch (error) {
      console.log("index.js:74 | error", error);
    }
  };

  const enterSplitMode = () => {
    setSplitMode(true);
    setSplitPayments([]);
  };

  const exitSplitMode = () => {
    setSplitMode(false);
    setSplitPayments([]);
  };

  const paySplit = (amount, paymentType, cardType) => {
    setSplitPayments((prev) => [
      ...prev,
      { amount, payment_type: paymentType, card_type: cardType },
    ]);
  };

  const removeSplit = (index) => {
    setSplitPayments((prev) => prev.filter((_, i) => i !== index));
  };

  const finalizeSplit = async () => {
    setSplitPaying(true);
    try {
      const paymentInput = {
        order_id: parseInt(order_id),
        payments: splitPayments.map((p) => ({
          amount: parseFloat(p.amount.toFixed(2)),
          type: p.payment_type,
          card_type: p.card_type || undefined,
        })),
        tip: tip || 0,
      };
      const { data } = await paySplitOrderMutation({
        variables: { payment: paymentInput },
      });
      const set_order = { ...order };
      set_order.payed = data.paySplitOrder.payed;
      set_order.cash_income = data.paySplitOrder.cash_income;
      set_order.tip = data.paySplitOrder.tip;
      set_order.payments = data.paySplitOrder.payments || [];
      setOrder({ ...set_order });

      if (settings.printer_autprint !== false) {
        printer.print(set_order, "client");
      }
      if (!settings.avoid_fulfilled) {
        updateOrder("fulfilled");
      }
      history.push("/");
    } catch (error) {
      console.log("index.js finalizeSplit error", error);
      toast.error(friendlyError(error));
    }
    setSplitPaying(false);
  };

  // --- Split bill by products ---
  const enterSplitBillMode = () => {
    setSplitBillMode(true);
    setSelectedProducts({});
    setBillSplits([]);
    setBillPaymentType("cash");
    setBillCardType(undefined);
  };

  const exitSplitBillMode = () => {
    setSplitBillMode(false);
    setSelectedProducts({});
    setBillSplits([]);
    localStorage.removeItem(splitBillStorageKey);
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  const getSelectedTotal = () => {
    if (!order.products) return 0;
    let total = 0;
    const selected = Object.keys(selectedProducts).filter((k) => selectedProducts[k]);
    selected.forEach((key) => {
      // key format: "productId_uN"
      const productId = key.split("_u")[0];
      const product = order.products.find((p) => String(p.id) === productId);
      if (product) {
        const unitPrice = product.qty > 1
          ? parseFloat(product.price) / product.qty
          : parseFloat(product.price);
        total += unitPrice;
      }
    });
    return total;
  };

  const getAlreadySplitProductIds = () => {
    const ids = {};
    billSplits.forEach((split) => {
      split.productIds.forEach((id) => { ids[id] = true; });
    });
    return ids;
  };

  const addBillSplit = () => {
    const selected = Object.keys(selectedProducts).filter((id) => selectedProducts[id]);
    if (selected.length === 0) return;
    const amount = getSelectedTotal();
    setBillSplits((prev) => [
      ...prev,
      {
        productIds: selected,
        amount,
        payment_type: billPaymentType,
        card_type: billCardType,
        tip: 0,
      },
    ]);
    setSelectedProducts({});
    setBillPaymentType("cash");
    setBillCardType(undefined);
  };

  const removeBillSplit = (index) => {
    setBillSplits((prev) => prev.filter((_, i) => i !== index));
  };

  const updateBillSplitTip = (index, value) => {
    setBillSplits((prev) =>
      prev.map((s, i) => {
        if (i !== index) return s;
        let tipValue = 0;
        if (value.includes("%")) {
          const pct = parseFloat(value.replace("%", ""));
          tipValue = isNaN(pct) ? 0 : Math.round(s.amount * pct) / 100;
        } else {
          const parsed = parseFloat(value);
          tipValue = isNaN(parsed) ? 0 : parsed;
        }
        return { ...s, tip: tipValue, tipInput: value };
      })
    );
  };

  const finalizeBillSplit = async () => {
    setSplitPaying(true);
    try {
      const totalTip = billSplits.reduce((sum, s) => sum + (s.tip || 0), 0);
      const paymentInput = {
        order_id: parseInt(order_id),
        payments: billSplits.map((s) => ({
          amount: parseFloat(s.amount.toFixed(2)),
          type: s.payment_type,
          card_type: s.card_type || undefined,
        })),
        tip: totalTip,
      };
      const { data } = await paySplitOrderMutation({
        variables: { payment: paymentInput },
      });
      const set_order = { ...order };
      set_order.payed = data.paySplitOrder.payed;
      set_order.cash_income = data.paySplitOrder.cash_income;
      set_order.tip = data.paySplitOrder.tip;
      set_order.payments = data.paySplitOrder.payments || [];
      setOrder({ ...set_order });

      if (settings.printer_autprint !== false) {
        printer.print(set_order, "client");
      }
      localStorage.removeItem(splitBillStorageKey);
      if (!settings.avoid_fulfilled) {
        updateOrder("fulfilled");
      }
      history.push("/");
    } catch (error) {
      console.log("index.js finalizeBillSplit error", error);
      toast.error(friendlyError(error));
    }
    setSplitPaying(false);
  };

  const printSplitBillTicket = () => {
    if (!billSplits.length) return;
    billSplits.forEach((split, i) => {
      const splitProducts = [];
      split.productIds.forEach((key) => {
        const productId = key.split("_u")[0];
        const product = order.products.find((p) => String(p.id) === productId);
        if (product) {
          const unitPrice = product.qty > 1
            ? parseFloat(product.price) / product.qty
            : parseFloat(product.price);
          splitProducts.push({
            ...product,
            qty: 1,
            price: unitPrice,
          });
        }
      });
      const splitOrder = {
        ...order,
        products: splitProducts,
        total: split.amount,
        subtotal: split.amount / 1.16,
        payment_type: split.payment_type,
        card_type: split.card_type,
        payments: [],
        split_bill_label: `--- ${lang("Cuenta", "Bill")} ${i + 1} ${lang("de", "of")} ${billSplits.length} ---`,
      };
      printer.print(splitOrder, "client");
    });
  };

  const toggleComandaSelectMode = () => {
    setComandaSelectMode((prev) => !prev);
    setComandaSelectedProducts({});
  };

  const toggleComandaProduct = (productId) => {
    setComandaSelectedProducts((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  const printComandaSelection = () => {
    const selected = Object.keys(comandaSelectedProducts).filter((k) => comandaSelectedProducts[k]);
    if (selected.length === 0) return;
    const selectedProductIds = selected.map((key) => key.split("_u")[0]);
    const filteredProducts = order.products.filter((p) => selectedProductIds.includes(String(p.id)));
    const comandaOrder = { ...order, products: filteredProducts };
    printer.print(comandaOrder, "restaurant", true);
    setComandaSelectMode(false);
    setComandaSelectedProducts({});
  };

  const handleBuyerType = async (ev) => {
    const current_buyer = { ...buyer };
    const type = ev.target.dataset.type;
    const value = ev.target.value;

    current_buyer[type] = value;
    setBuyer(current_buyer);
  };

  const saveUserPromt = async (ev) => {
    getClients({
      variables: {
        limit: 5,
      },
    });
    modalHandler("adduser");
  };

  const saveUser = async (ev) => {
    const set_order = { ...order };
    try {
      const userInput = {
        id: buyer.id || null,
        name: buyer.name,
        lastname: buyer.lastname,
        phone: buyer.phone,
        email: buyer.email || null,
      };
      const new_client = await saveUserMutation({
        variables: { user: userInput, order_id: parseInt(order_id) },
      });
      set_order.client = new_client.data.saveUser;
      setOrder({ ...set_order });
      modalHandler("adduserdata");
    } catch (error) {
      console.log("index.js:74 | error", error);
    }
  };

  const selectClient = (ev) => {
    let client_key = ev.currentTarget.dataset.client_key;
    if (!client_key) {
      client_key = 0;
    } else {
      const selBuyer = clients.getClients[client_key];
      setBuyer({ ...selBuyer });
    }
    modalHandler("adduserdata");
  };

  const searchUserPromt = (input) => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const text = input.target.value;
    const current_buyer = { ...buyer };
    current_buyer.phone = text;
    setBuyer(current_buyer);

    // Only search if there's actual text (more than 2 characters)
    if (text && text.length >= 2) {
      timerRef.current = setTimeout(() => {
        getClients({
          variables: {
            search: text,
            limit: 5,
          },
        });
      }, 800); // Increased debounce time to 800ms
    }
  };

  const handleTipAdded = (newTip) => {
    setOrder((prev) => ({ ...prev, tip: newTip }));
  };

  const updateOrder = async (status, restoreInventory) => {
    if (!status) return;

    // Cancelling an order that has recipe-tracked products needs a decision
    // from the staff: were the ingredients already used (product prepared,
    // customer never showed up) or should they go back to inventory? Ask
    // via a popup instead of silently always restoring stock.
    if (
      status === "cancelled" &&
      restoreInventory === undefined &&
      (order.products || []).some((p) => p.product?.has_recipe)
    ) {
      setModals({ cancelOrder: true });
      return;
    }

    try {
      const updateOrderInput = {
        order_id: parseInt(order.id),
        status,
        ...(restoreInventory !== undefined ? { restoreInventory } : {}),
      };
      await updateOrderMutation({ variables: { order: updateOrderInput } });

      history.push("/");
    } catch (error) {
      console.log("index.js:90 | error", error);
    }
  };

  const confirmCancelOrder = (restoreInventory) => {
    setModals({});
    updateOrder("cancelled", restoreInventory);
  };

  const modalHandler = (type) => {
    const curreent_modals = { ...modals };
    const new_modals = {};
    new_modals[type] = !curreent_modals[type];
    setModals({ ...new_modals });
  };

  const promtChangeTable = () => {
    setModals({
      tables: true,
    });
  };

  const changeTable = async (ev) => {
    const table_id = ev.currentTarget.dataset.table_id;
    const selected_table = tables.find((t) => t.id === table_id);

    try {
      const updateOrderInput = {
        order_id: parseInt(order.id),
        table_id: +table_id,
      };
      await updateTableMutation({ variables: { order: updateOrderInput } });
      const set_order = { ...order };

      set_order.table.id = table_id;
      set_order.table.name = selected_table.name || `#${table_id}`;
      setOrder({ ...set_order });
      setModals({});
    } catch (error) {
      console.log("index.js:105 | error", error);
    }
  };

  const openPaymentLinkModal = () => {
    const clientEmail = order.client?.email || "";
    const clientName = order.client
      ? `${order.client.name || ""} ${order.client.lastname || ""}`.trim()
      : "";
    setPaymentLinkEmail(clientEmail);
    setPaymentLinkName(clientName);
    modalHandler("paymentLink");
  };

  const sendPaymentLink = async () => {
    if (!paymentLinkEmail || !paymentLinkEmail.trim()) {
      toast.error(lang("Ingresa el email del cliente", "Enter customer email"));
      return;
    }
    try {
      const result = await createPaymentLinkMutation({
        variables: {
          amount: parseFloat(order.total),
          description: `Orden #${order.id}`,
          customer_email: paymentLinkEmail.trim(),
          customer_name: paymentLinkName.trim() || null,
        },
      });
      if (result.data?.createPaymentLink.emailSent) {
        toast.success(
          lang("Link de cobro enviado por email", "Payment link sent by email")
        );
      } else {
        toast.warning(
          lang(
            "Link creado pero no se pudo enviar el email",
            "Link created but email could not be sent"
          )
        );
      }
      modalHandler("paymentLink");
    } catch (error) {
      toast.error(
        error.message || lang("Error al enviar link de cobro", "Error sending payment link")
      );
    }
  };

  const productAsDone = (ev) => {
    const product_id = ev.currentTarget.dataset.product_id;
    const set_order = { ...order };
    const product_index = set_order.products.findIndex(
      (p) => p.id === product_id,
    );
    let status = set_order.products[product_index].status;
    let new_status;

    switch (status) {
      case "1":
        new_status = "0";
        break;

      default:
        new_status = "1";
        break;
    }
    const productInput = {
      id: parseInt(product_id),
      status: new_status,
    };
    updateProductMutation({
      variables: {
        product: productInput,
      },
    });
    const qty_done = set_order.products[product_index].qty_done
      ? set_order.products[product_index].qty_done + 1
      : 1;
    set_order.products[product_index].qty_done = qty_done;
    if (
      set_order.products[product_index].qty ===
      set_order.products[product_index].qty_done
    ) {
      set_order.products[product_index].status = 1;
    }
    setOrder({ ...set_order });
  };

  const renderUI = () => {
    switch (status) {
      case "payout":
        if (splitMode) {
          return (
            <SplitPayout
              order={order}
              tip={tip}
              splitPayments={splitPayments}
              onPaySplit={paySplit}
              onRemoveSplit={removeSplit}
              onCancel={exitSplitMode}
              onFinalize={finalizeSplit}
              paying={splitPaying}
            />
          );
        }
        return (
          <PayOut
            order={order}
            addUp={addUp}
            calculator={calculator}
            payOutBack={payOutBack}
            payOrder={payOrder}
            shake={shake}
            setPaymentType={setPaymentType}
            tip={tip}
            onTipChange={handleTipChange}
            tipInput={tipInput}
            tipFocused={tipFocused}
            setTipFocused={setTipFocused}
            onSplitMode={enterSplitMode}
            sendPaymentLink={openPaymentLinkModal}
          />
        );

      default:
        return (
          <OrderPage
            loading={loading}
            order={order}
            payOut={payOut}
            updateOrder={updateOrder}
            saveUser={saveUserPromt}
            changeTable={promtChangeTable}
            productAsDone={productAsDone}
            print={printer.print}
            sendPaymentLink={openPaymentLinkModal}
            onTipAdded={handleTipAdded}
            comandaSelectMode={comandaSelectMode}
            comandaSelectedProducts={comandaSelectedProducts}
            toggleComandaSelectMode={toggleComandaSelectMode}
            toggleComandaProduct={toggleComandaProduct}
            printComandaSelection={printComandaSelection}
            splitBillMode={splitBillMode}
            enterSplitBillMode={enterSplitBillMode}
            exitSplitBillMode={exitSplitBillMode}
            selectedProducts={selectedProducts}
            toggleProductSelection={toggleProductSelection}
            getSelectedTotal={getSelectedTotal}
            getAlreadySplitProductIds={getAlreadySplitProductIds}
            billSplits={billSplits}
            addBillSplit={addBillSplit}
            removeBillSplit={removeBillSplit}
            finalizeBillSplit={finalizeBillSplit}
            splitPaying={splitPaying}
            billPaymentType={billPaymentType}
            setBillPaymentType={setBillPaymentType}
            billCardType={billCardType}
            setBillCardType={setBillCardType}
            settings={settings}
            printSplitBillTicket={printSplitBillTicket}
            updateBillSplitTip={updateBillSplitTip}
          />
        );
    }
  };

  if (order_loading) {
    return <OrderLoader />;
  }

  return (
    <div>
      {isMobile && status === null ? (
        <NavMob text="Órdenes Activas" link="/" />
      ) : null}
      <div className="container-fluid py-md-3">
        <div className="row">
          <div className="col-lg-12 min_vh_5 " id="page_content">
            {order ? renderUI() : <p>Loading</p>}
          </div>
        </div>
      </div>
      {modals.tables && (
        <ModalTables
          open={modals.tables}
          tables={tables}
          toggle={() => modalHandler("tables")}
          makeOrder={changeTable}
          active_table={order.table ? order.table.id : null}
        />
      )}
      {modals.adduser && (
        <AddUserModal
          open={modals.adduser}
          toggle={() => modalHandler("adduser")}
          keyupFun={searchUserPromt}
          selectClient={selectClient}
          users={clients ? clients.getClients : []}
        />
      )}
      {modals.adduserdata && (
        <AddUserDataModal
          open={modals.adduserdata}
          modalHandler={modalHandler}
          buyer={buyer}
          nextFun={saveUser}
          save
          toggle={() => modalHandler("adduserdata")}
          handleType={handleBuyerType}
        />
      )}
      {modals.cancelOrder && (
        <CancelOrderModal
          open={modals.cancelOrder}
          toggle={() => modalHandler("cancelOrder")}
          onConfirm={confirmCancelOrder}
        />
      )}
      {modals.paymentLink && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => modalHandler("paymentLink")}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content" style={{ borderRadius: 12 }}>
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title" style={{ fontWeight: 700 }}>
                  {lang("Enviar Link de Cobro", "Send Payment Link")}
                </h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => modalHandler("paymentLink")}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div
                  className="text-center mb-3 p-3"
                  style={{ background: "#f8f9fa", borderRadius: 8 }}
                >
                  <small className="text-muted">
                    {lang("Orden", "Order")} #{order.id}
                  </small>
                  <h3 className="mb-0" style={{ fontWeight: 700 }}>
                    ${parseFloat(order.total).toFixed(2)}
                  </h3>
                </div>
                <div className="form-group">
                  <label style={{ fontWeight: 600, fontSize: 13 }}>
                    {lang("Email del cliente", "Customer email")} *
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    value={paymentLinkEmail}
                    onChange={(e) => setPaymentLinkEmail(e.target.value)}
                    placeholder="cliente@email.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label style={{ fontWeight: 600, fontSize: 13 }}>
                    {lang("Nombre del cliente", "Customer name")}{" "}
                    <span style={{ color: "#999", fontWeight: 400 }}>
                      ({lang("opcional", "optional")})
                    </span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={paymentLinkName}
                    onChange={(e) => setPaymentLinkName(e.target.value)}
                    placeholder={lang("Nombre", "Name")}
                  />
                </div>
                <p style={{ fontSize: 12, color: "#999" }}>
                  {lang(
                    "Se enviara un link de pago seguro por email. El cliente podra pagar con tarjeta directamente en Stripe.",
                    "A secure payment link will be sent by email. The customer can pay by card directly on Stripe."
                  )}
                </p>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button
                  className="btn"
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 6,
                  }}
                  onClick={() => modalHandler("paymentLink")}
                >
                  {lang("Cancelar", "Cancel")}
                </button>
                <button
                  className="btn"
                  disabled={creatingLink}
                  style={{
                    background: "#FFBB28",
                    color: "#292929",
                    fontWeight: 600,
                    borderRadius: 6,
                  }}
                  onClick={sendPaymentLink}
                >
                  {creatingLink ? (
                    <span className="spinner-border spinner-border-sm mr-2" />
                  ) : (
                    <i className="fas fa-paper-plane mr-2" />
                  )}
                  {lang("Enviar", "Send")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const OrderLoader = () => (
  <div className="min_vh_9 d-flex justify-content-center align-items-center">
    <LoaderDots size="20" color="#b5bcc2" delay="2" />
  </div>
);

export default Order;
